/**
 * Effective Permissions Viewer
 * Full-page view showing the resolved effective permissions for a specific user
 * across all their assigned roles, page access, and program access.
 * Includes navigation back to user management and link to access control.
 */

import { useTabbedChrome } from '@/components/weavein';
import React, { useState, useEffect } from 'react';
import {
  Eye, ArrowLeft, Star, CheckCircle, XCircle, ChevronDown, ChevronRight,
  Shield, FolderOpen, Settings, ExternalLink,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Progress } from '../ui/progress';
import { apiClient } from '@/services/apiClient';

// ── Types ──────────────────────────────────────────────────────────────────────

interface EffectivePermissionsViewerProps {
  userId: number;
  userName: string;
  cboId: number;
  onBack: () => void;
  onNavigate?: (tab: string) => void;
}

interface RoleInfo {
  role_type: string;
  display_name_en: string;
  system_role: string;
  access_level: number;
  is_primary: boolean;
}

interface PagePermission {
  page_key: string;
  page_name: string;
  category: string;
  has_access: boolean;
  view_access: boolean;
  edit_access: boolean;
  delete_access: boolean;
  approve_access: boolean;
  export_access: boolean;
}

interface ProgramAccess {
  program_id: number;
  program_name: string;
  access_level: 'full' | 'view_only';
}

interface EffectivePermissionsResponse {
  user_id: number;
  organization_id: number;
  roles: RoleInfo[];
  is_admin: boolean;
  page_permissions: PagePermission[];
  program_access: ProgramAccess[];
}

// ── Category display labels ────────────────────────────────────────────────────

const CATEGORY_LABELS: Record<string, string> = {
  core: 'Core',
  member_management: 'Member Management',
  enrollments: 'Enrollments',
  care_coordination: 'Care Coordination',
  communications: 'Communications',
  analytics: 'Analytics & Reporting',
  billing: 'Billing & Financials',
  incentives: 'Incentives & Rewards',
  programs: 'Programs',
  workflows: 'Workflows',
  team: 'Team & Audits',
  audits: 'Audits & Compliance',
  administration: 'Administration',
  integrations: 'Integrations',
  employer: 'Employer Platform',
  employee_portal: 'Employee Portal',
  medicaid: 'Medicaid',
  weavein: 'Marketing & Sales',
};

// ── Component ──────────────────────────────────────────────────────────────────

export function EffectivePermissionsViewer({
  userId,
  userName,
  cboId,
  onBack,
  onNavigate,
}: EffectivePermissionsViewerProps) {
  const tabbed = useTabbedChrome();
  const [data, setData] = useState<EffectivePermissionsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>({});

  useEffect(() => {
    let cancelled = false;

    async function fetchPermissions() {
      setLoading(true);
      setError(null);
      try {
        const response = await apiClient.get(
          `/api/cbos/${cboId}/users/${userId}/effective-permissions`,
          { headers: { 'X-CBO-ID': cboId.toString() } }
        );
        if (!cancelled) {
          setData(response.data);
        }
      } catch (err: any) {
        if (!cancelled) {
          setError(
            err.response?.data?.detail ||
            err.message ||
            'Failed to load effective permissions'
          );
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    fetchPermissions();
    return () => { cancelled = true; };
  }, [userId, cboId]);

  // Group page permissions by category
  const groupedPages = data
    ? data.page_permissions.reduce<Record<string, PagePermission[]>>((acc, perm) => {
        const cat = perm.category || 'core';
        if (!acc[cat]) acc[cat] = [];
        acc[cat].push(perm);
        return acc;
      }, {})
    : {};

  const toggleCategory = (category: string) => {
    setExpandedCategories(prev => ({ ...prev, [category]: !prev[category] }));
  };

  // Summary stats
  const totalPages = data ? data.page_permissions.length : 0;
  const accessiblePages = data ? data.page_permissions.filter(p => p.has_access).length : 0;
  const blockedPages = totalPages - accessiblePages;

  return (
    <div className="space-y-6">
      {/* ── Header with back navigation ──────────────────────────── */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={onBack} className="gap-1">
            <ArrowLeft className="w-4 h-4" />
            Back to User Management
          </Button>
          {!tabbed && <div className="h-6 w-px bg-gray-200 dark:bg-gray-700" />}
          {!tabbed && (
          <div className="flex items-center gap-2">
            <Eye className="w-5 h-5 text-blue-600" />
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
              Effective Permissions
            </h2>
          </div>
          )}
        </div>
        {onNavigate && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => onNavigate('access-control')}
            className="gap-1"
          >
            <Settings className="w-4 h-4" />
            Access Control
            <ExternalLink className="w-3 h-3" />
          </Button>
        )}
      </div>

      {/* ── User info bar ────────────────────────────────────────── */}
      <Card>
        <CardContent className="py-4">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{userName}</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">User ID: {userId}</p>
            </div>
            <div className="flex items-center gap-4 text-sm">
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">{accessiblePages}</div>
                <div className="text-gray-500 dark:text-gray-400">Accessible</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-red-500">{blockedPages}</div>
                <div className="text-gray-500 dark:text-gray-400">Blocked</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-700 dark:text-gray-300">{totalPages}</div>
                <div className="text-gray-500 dark:text-gray-400">Total Pages</div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Loading state */}
      {loading && (
        <div className="flex items-center justify-center py-16">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        </div>
      )}

      {/* Error state */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 text-red-700 dark:text-red-300 text-sm">
          {error}
        </div>
      )}

      {/* Data loaded */}
      {data && !loading && (
        <>
          {/* ── Roles Section ─────────────────────────────────────── */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Shield className="w-4 h-4 text-indigo-500" />
                Assigned Roles
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {data.roles.map((role, idx) => (
                  <Badge
                    key={idx}
                    variant={role.is_primary ? 'default' : 'secondary'}
                    className="flex items-center gap-1 py-1 px-3"
                  >
                    {role.is_primary && <Star className="w-3 h-3" />}
                    {role.display_name_en}
                    <span className="text-xs opacity-60 ml-1">
                      ({role.system_role} &middot; L{role.access_level})
                    </span>
                  </Badge>
                ))}
                {data.is_admin && (
                  <Badge variant="destructive" className="flex items-center gap-1 py-1 px-3">
                    <Shield className="w-3 h-3" />
                    Admin — Full Access
                  </Badge>
                )}
              </div>
            </CardContent>
          </Card>

          {/* ── Page Access Section ───────────────────────────────── */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <FolderOpen className="w-4 h-4 text-green-600" />
                  Page Access by Category
                </CardTitle>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-xs"
                  onClick={() => {
                    const allExpanded = Object.keys(groupedPages).every(k => expandedCategories[k]);
                    const next: Record<string, boolean> = {};
                    Object.keys(groupedPages).forEach(k => { next[k] = !allExpanded; });
                    setExpandedCategories(next);
                  }}
                >
                  {Object.keys(groupedPages).every(k => expandedCategories[k]) ? 'Collapse All' : 'Expand All'}
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-2">
              {Object.entries(groupedPages).map(([category, pages]) => {
                const accessibleCount = pages.filter(p => p.has_access).length;
                const totalCount = pages.length;
                const pct = totalCount > 0 ? Math.round((accessibleCount / totalCount) * 100) : 0;
                const expanded = !!expandedCategories[category];
                const label = CATEGORY_LABELS[category] || category;

                return (
                  <div key={category} className="border border-gray-100 dark:border-gray-700 rounded-lg overflow-hidden">
                    {/* Category header row */}
                    <button
                      onClick={() => toggleCategory(category)}
                      className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors text-left"
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        {expanded
                          ? <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
                          : <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        }
                        <span className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate">
                          {label}
                        </span>
                        <Badge
                          variant="outline"
                          className={
                            accessibleCount === totalCount
                              ? 'text-green-600 border-green-200 dark:border-green-800'
                              : accessibleCount === 0
                              ? 'text-red-500 border-red-200 dark:border-red-800'
                              : 'text-yellow-600 border-yellow-200 dark:border-yellow-800'
                          }
                        >
                          {accessibleCount}/{totalCount}
                        </Badge>
                      </div>
                      <div className="w-32 flex-shrink-0 ml-3">
                        <Progress value={pct} className="h-2" />
                      </div>
                    </button>

                    {/* Expanded detail */}
                    {expanded && (
                      <div className="border-t border-gray-100 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-900/30">
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-700">
                              <th className="text-left px-4 py-2 font-medium">Page</th>
                              <th className="px-2 py-2 font-medium text-center">Access</th>
                              <th className="px-2 py-2 font-medium text-center">View</th>
                              <th className="px-2 py-2 font-medium text-center">Edit</th>
                              <th className="px-2 py-2 font-medium text-center">Delete</th>
                              <th className="px-2 py-2 font-medium text-center">Approve</th>
                              <th className="px-2 py-2 font-medium text-center">Export</th>
                            </tr>
                          </thead>
                          <tbody>
                            {pages.map((page) => (
                              <tr
                                key={page.page_key}
                                className="border-b border-gray-100 dark:border-gray-700 last:border-0"
                              >
                                <td className="px-4 py-2 text-gray-700 dark:text-gray-300">
                                  {page.page_name}
                                </td>
                                <td className="px-2 py-2 text-center">
                                  <PermissionIcon granted={page.has_access} />
                                </td>
                                <td className="px-2 py-2 text-center">
                                  <PermissionIcon granted={page.view_access} />
                                </td>
                                <td className="px-2 py-2 text-center">
                                  <PermissionIcon granted={page.edit_access} />
                                </td>
                                <td className="px-2 py-2 text-center">
                                  <PermissionIcon granted={page.delete_access} />
                                </td>
                                <td className="px-2 py-2 text-center">
                                  <PermissionIcon granted={page.approve_access} />
                                </td>
                                <td className="px-2 py-2 text-center">
                                  <PermissionIcon granted={page.export_access} />
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                );
              })}

              {Object.keys(groupedPages).length === 0 && (
                <p className="text-sm text-gray-500 dark:text-gray-400 italic">
                  No page permissions found.
                </p>
              )}
            </CardContent>
          </Card>

          {/* ── Program Access Section ────────────────────────────── */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <FolderOpen className="w-4 h-4 text-purple-500" />
                Program Access
              </CardTitle>
            </CardHeader>
            <CardContent>
              {data.program_access.length > 0 ? (
                <div className="space-y-2">
                  {data.program_access.map((prog) => (
                    <div
                      key={prog.program_id}
                      className="flex items-center justify-between px-3 py-2 rounded-md bg-gray-50 dark:bg-gray-700/40"
                    >
                      <span className="text-sm text-gray-800 dark:text-gray-200">
                        {prog.program_name}
                      </span>
                      <Badge
                        variant={prog.access_level === 'full' ? 'default' : 'secondary'}
                        className={
                          prog.access_level === 'full'
                            ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300'
                            : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300'
                        }
                      >
                        {prog.access_level === 'full' ? 'Full' : 'View Only'}
                      </Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500 dark:text-gray-400 italic">
                  No program-level access configured.
                </p>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}

// ── Helper ─────────────────────────────────────────────────────────────────────

function PermissionIcon({ granted }: { granted: boolean }) {
  return granted ? (
    <CheckCircle className="w-4 h-4 text-green-500 inline-block" />
  ) : (
    <XCircle className="w-4 h-4 text-gray-300 dark:text-gray-600 inline-block" />
  );
}
